import { useMemo, useState } from 'react';
import { Filter, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/lib/AuthContext';
import { CHANGELOG, KIND_LABEL, type ChangeKind } from '@/content/changelog';
import { cn } from '@/lib/utils';

const KIND_STYLE: Record<ChangeKind, { badge: 'success' | 'secondary' | 'warning'; dot: string }> = {
  novinka: { badge: 'success', dot: 'bg-emerald-500' },
  vylepseni: { badge: 'secondary', dot: 'bg-sky-500' },
  oprava: { badge: 'warning', dot: 'bg-amber-500' },
};

const KIND_ORDER: ChangeKind[] = ['novinka', 'vylepseni', 'oprava'];

const dateFmt = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' });

function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : dateFmt.format(d);
}

/**
 * Novinky v aplikaci — casova osa vydani. Obsah je staticky
 * (`src/content/changelog.ts`), takze stranka nepotrebuje zadne API ani
 * opravneni: co je noveho, ma videt kazdy prihlaseny. Provozni poznamky
 * (`adminNote`) se zobrazuji jen adminovi.
 */
export function NovinkyPage() {
  const { isAdmin } = useAuth();
  const [kind, setKind] = useState<ChangeKind | 'vse'>('vse');

  const entries = useMemo(() => {
    const sorted = [...CHANGELOG].sort((a, b) => b.date.localeCompare(a.date));
    if (kind === 'vse') return sorted;
    return sorted
      .map((e) => ({ ...e, items: e.items.filter((i) => i.kind === kind) }))
      .filter((e) => e.items.length > 0);
  }, [kind]);

  const counts = useMemo(() => {
    const all = CHANGELOG.flatMap((e) => e.items);
    return {
      vse: all.length,
      novinka: all.filter((i) => i.kind === 'novinka').length,
      vylepseni: all.filter((i) => i.kind === 'vylepseni').length,
      oprava: all.filter((i) => i.kind === 'oprava').length,
    };
  }, []);

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <Sparkles className="h-5 w-5 text-primary" />
          Novinky v aplikaci
        </h1>
        <p className="text-sm text-muted-foreground">
          Co se v Denní operativě mění — nejnovější nahoře. {counts.vse} změn v {CHANGELOG.length}{' '}
          vydáních.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {(['vse', ...KIND_ORDER] as const).map((k) => (
          <Button
            key={k}
            size="sm"
            variant={kind === k ? 'default' : 'outline'}
            onClick={() => setKind(k)}
          >
            {k === 'vse' ? 'Vše' : KIND_LABEL[k]}
            <span className="ml-2 text-xs opacity-70">{counts[k]}</span>
          </Button>
        ))}
      </div>

      <ol className="relative space-y-5 border-l pl-6">
        {entries.map((entry, index) => (
          <li key={`${entry.date}-${entry.title}`} className="relative">
            {/* bod na casove ose */}
            <span
              className={cn(
                'absolute -left-[31px] top-3 h-3 w-3 rounded-full ring-4 ring-white',
                index === 0 && kind === 'vse' ? 'bg-primary' : 'bg-muted-foreground/40',
              )}
            />
            <Card className={cn(index === 0 && kind === 'vse' && 'border-primary/40 shadow-sm')}>
              <CardContent className="space-y-4 p-5">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h2 className="text-base font-semibold leading-tight">{entry.title}</h2>
                  <span className="text-xs text-muted-foreground">{formatDate(entry.date)}</span>
                  {index === 0 && kind === 'vse' && <Badge>Nejnovější</Badge>}
                </div>

                <ul className="space-y-3">
                  {[...entry.items]
                    .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind))
                    .map((item) => (
                      <li key={item.title} className="flex gap-3">
                        <span
                          className={cn(
                            'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                            KIND_STYLE[item.kind].dot,
                          )}
                          aria-hidden
                        />
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium">{item.title}</span>
                            <Badge variant={KIND_STYLE[item.kind].badge}>
                              {KIND_LABEL[item.kind]}
                            </Badge>
                          </div>
                          {item.detail && (
                            <p className="text-sm leading-relaxed text-muted-foreground">
                              {item.detail}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                </ul>

                {/* Provozni detaily zajimaji jen spravce — ostatnim by jen zavazely. */}
                {entry.adminNote && isAdmin && (
                  <div className="rounded-md border border-dashed bg-muted/40 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Pro správce
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {entry.adminNote}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </li>
        ))}
      </ol>

      {entries.length === 0 && (
        <p className="text-sm text-muted-foreground">Pro zvolený filtr nejsou žádné záznamy.</p>
      )}
    </div>
  );
}
